import React, { useState, type PropsWithChildren } from 'react';
import { EntryPointContainer } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { AnonymousSidebar } from '@jira/navigation-apps__sidebars-anonymous/src/AnonymousSidebar.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { LEFT_SIDEBAR } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import {
	getNav4SidebarId,
	useGetParamsForGetNav4SidebarId,
} from '@jira/navigation-apps__sidebar-nav4-sidebar-selector/src/common/logic.tsx';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import { ReportSSRFailOnUse } from '@jira/ssr__rendering-guard/src/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { Skeleton as ProjectSettingsSkeleton } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-project-common/src/common/ui/skeleton/index.tsx';
import type { SidebarId } from '@jira/platform__routing-route-types/src/Navigation.tsx';
import { SidebarNav4ContextProvider } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { NavigateBackProvider } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-back-button/src/controllers/navigate-back-context/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { GlobalSideNavigationSidebarSelector } from './GlobalSideNavigationSidebarSelector.tsx';

const EMPTY_PROPS_OBJ = {} as const;

/**
 * Adding default export to support importCond() usage in PageContainer.
 */
export default NavPlatformSidebar;

/**
 * This component is equivalent to src/packages/navigation-apps/sidebar-nav4-sidebar-selector/src/ui/entrypoint/index.tsx
 *
 * If a route is NOT using entrypoints, render GlobalSideNavigationSidebarSelector.
 *
 * If a route IS using entrypoints, render the sidebar EP that is specified in the route config.
 *
 */
export function NavPlatformSidebar(): React.JSX.Element | null {
	/**
	 * The values returned by these hooks on the initial-render are what the useState ->C omponent uses
	 * until we change sidebars. This is by design. It keeps Component stable.
	 *
	 * Leaving useCurrentRoute() outside (of Component) means the stored component keeps the initial
	 * route via closure, so the sidebar stays stable across route changes.
	 *
	 * Moving useCurrentRoute() inside makes the stored component react to every navigation, which causes
	 * the sidebar subtree to update/remount during transitions, so the Spaces menu’s open state gets
	 * reset and you see it collapse then reopen.
	 */
	const currentRoute = useCurrentRoute();
	const paramsForGetNav4SidebarId = useGetParamsForGetNav4SidebarId();
	const currentSidebarId = getNav4SidebarId(paramsForGetNav4SidebarId);
	const currentEntryPointRef = usePageLayoutEntryPoint(LEFT_SIDEBAR);
	const isPageHydrating = useIsHydrateRenderMode();

	const [sidebarConfig, setSidebarConfig] = useState<{
		Component: React.FC;
		sidebarId: SidebarId;
	}>(() => {
		// We want to avoid switching between the GlobalSideNavigationSidebarSelector and the sidebar entryPoint as we navigate through the SPA.
		// To do that, we need to keep using the __initial__ sidebar entryPoint if we haven't changed sidebars.
		// When we do switch sidebars, keep using GlobalSideNavigationSidebarSelector from that point onwards (for now).
		// When all of the sidebars are using entryPoints, we can deprecate the GlobalSideNavigationSidebarSelector.
		return {
			Component: (): React.JSX.Element | null => {
				// When we are dealing with an anonymous user, immediately render the anonymous sidebar.
				// The Nav4 entrypoint selector doesn't do this early-exit.
				if (paramsForGetNav4SidebarId.isAnonymous) {
					return <AnonymousSidebar />;
				}

				const extraEPRefPlaceholderProps = {
					isSSRPlaceholderEnabled: !isPageHydrating
						? currentRoute.entryPointOptions?.isSSRPlaceholderEnabled
						: undefined,
				};

				return currentEntryPointRef && currentRoute.navigation?.sidebarId === currentSidebarId ? (
					<UFOSegment name="gsn-sidebar-entrypoint">
						<JSErrorBoundary
							fallback="flag"
							id="gsn-sidebar-entrypoint"
							packageName="nav-platform-integration"
						>
							<EntryPointReferencePlaceholder
								name="gsn-sidebar-entrypoint-placeholder"
								fallback={<ProjectSettingsSkeleton />}
								entryPointReference={currentEntryPointRef}
								isPlaceholderForPageLayoutComponent
								{...extraEPRefPlaceholderProps}
							>
								<EntryPointContainer
									entryPointReference={currentEntryPointRef}
									// oxlint-disable-next-line typescript/consistent-type-assertions
									props={EMPTY_PROPS_OBJ as never}
								/>
							</EntryPointReferencePlaceholder>
						</JSErrorBoundary>
					</UFOSegment>
				) : (
					<GlobalSideNavigationSidebarSelectorWithSuspense />
				);
			},
			sidebarId: currentSidebarId,
		};
	});

	// This is an inexpensive comparison (no need to use useEffect - in fact ESLint warns against doing so)
	if (sidebarConfig.sidebarId !== currentSidebarId) {
		setSidebarConfig({
			Component: GlobalSideNavigationSidebarSelectorWithSuspense,
			sidebarId: currentSidebarId,
		});
	}

	return (
		<NavPlatWrapper>
			<sidebarConfig.Component />
		</NavPlatWrapper>
	);
}

const GlobalSideNavigationSidebarSelectorWithSuspense: React.FC = () => (
	<UFOSegment name="nav4.sidebar-global-side-navigation-sidebar-selector">
		<Placeholder
			name="nav4-ignore.sidebar-global-side-navigation-sidebar-selector"
			fallback={
				<ReportSSRFailOnUse pageId="navigation" fallbackId="Sidebar" displayErrorInStaging />
			}
		>
			<GlobalSideNavigationSidebarSelector />
		</Placeholder>
	</UFOSegment>
);

// Both the content sidebar and the other sidebars use SidebarNav4ContextProvider.
// Therefore, it makes sense to
function NavPlatWrapper({ children }: PropsWithChildren) {
	return (
		<NavigateBackProvider>
			<SidebarNav4ContextProvider>{children}</SidebarNav4ContextProvider>
		</NavigateBackProvider>
	);
}
